import React from 'react';

import { useSelector } from 'react-redux';
import { getDashboardsState, getIsSelfHosted } from '@/store-selectors';
import {
  Button,
  ButtonGroup,
  Island,
  IslandButton,
  TrevorIcon,
} from '@trevor/ui-components';
import { refreshFrequency } from '@/util/helpers/time-helpers';
import { AddTextButton } from '@/features/dashboard/components/AddTextButton';
import styled from 'styled-components';
import { useDashboardId, useDatasourceId } from '@/hooks';
import { openEditDashboardModal, refreshDashboard } from '@/actions';
import { useAppDispatch } from '@/store/hooks';
import DashboardRef from '@/util/DashboardRef';
import { AddWidgetButton } from '@/features/dashboard/components/AddWidgetButton';
import { useGetInvitesQuery } from '@/features/dashboardShare/ShareDropdown/InviteTab/inviteApi';
import { DashboardStickers } from '@/features/dashboard/components/DashboardHeader/PrivateDashboardHeader/DashboardStickers';
import { RefreshInfo } from '@/features/dashboard/components/DashboardHeader/PrivateDashboardHeader/RefreshInfo';
import { DashboardBreadcrumb } from '@/features/dashboard/components/DashboardHeader/PrivateDashboardHeader/DashboardBreadcrumb';
import { usePermission } from '@/hooks/usePermission';
import { invalidateSavedQueryPresetsAction } from '@/features/saved-query-dashboard-filter-presets/apiSlice';

const HeaderContainer = styled.div`
  padding: var(--spacing-m) var(--spacing-l) var(--spacing-m)
    calc(var(--spacing-m) * 2);
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-interface-layout-darkest);
`;

const HeaderLeftContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-s);
`;

const HeaderRightContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-s);
`;

type PrivateDashboardHeaderProps = {
  onFullScreen: F0;
  // TODO: probably not needed
  // userInteraction: F<unknown, F<unknown>>;
  // updateDashboardFilters: F<[]>;
};

export const PrivateDashboardHeader: React.FC<PrivateDashboardHeaderProps> = ({
  onFullScreen,
}) => {
  const dashboardState = useSelector(getDashboardsState);
  const datasourceId = useDatasourceId();
  const dashboardId = useDashboardId();
  const isSelfHosted = useSelector(getIsSelfHosted);
  const dispatch = useAppDispatch();
  const readonly = !usePermission(
    `${datasourceId}.dashboards.${dashboardId}.write`,
  );
  const { data: invitedUsers = [] } = useGetInvitesQuery(
    {
      datasourceId,
      dashboardId,
    },
    { skip: readonly },
  );
  const dashboard = dashboardState.active;

  if (!dashboard) return null;

  const onEditDashboard =
    (dashboardToEdit: DashboardRef) => (e: React.MouseEvent) => {
      e.preventDefault();
      dispatch(openEditDashboardModal(dashboardToEdit));
    };

  const onRefreshClicked = (e: unknown) => {
    dispatch(refreshDashboard(true, null, e));
    dispatch(invalidateSavedQueryPresetsAction);
  };

  return (
    <HeaderContainer>
      <HeaderLeftContainer>
        <DashboardBreadcrumb
          datasourceId={datasourceId}
          dashboardId={dashboardId}
          title={dashboard.name()}
          onTitleClick={onEditDashboard(dashboard)}
        />
        {!isSelfHosted && (
          <DashboardStickers
            datasourceId={datasourceId}
            dashboardId={dashboardId}
            isPublic={dashboardState.public}
            isEmbedded={dashboard.isEmbedded}
            numberOfGuestUsers={invitedUsers.length}
          />
        )}
      </HeaderLeftContainer>
      <HeaderRightContainer>
        {dashboardState.chartWidgets && (
          <RefreshInfo
            lastRefreshed={dashboardState.lastRefreshed}
            frequencyRefresh={refreshFrequency(dashboard)}
          />
        )}
        {readonly ? (
          dashboardState.chartWidgets && (
            <Button
              variant="secondary"
              icon={<TrevorIcon name="refresh" />}
              onClick={onRefreshClicked}
            />
          )
        ) : (
          <>
            <Island size="big">
              {dashboardState.chartWidgets && (
                <IslandButton
                  onClick={onRefreshClicked}
                  content={<TrevorIcon name="refresh" />}
                  tooltipContent="Refresh dashboard"
                />
              )}

              <IslandButton
                onClick={onFullScreen}
                content={<TrevorIcon name="expand" />}
                tooltipContent="Enter fullscreen mode"
              />

              <IslandButton
                onClick={onEditDashboard(dashboard)}
                content={<TrevorIcon name="settings" />}
                tooltipContent="Edit dashboard settings"
              />
            </Island>

            <ButtonGroup>
              <AddTextButton />
              {dashboardState.chartWidgets && dashboardState.textWidgets && (
                <AddWidgetButton />
              )}
            </ButtonGroup>
          </>
        )}
      </HeaderRightContainer>
    </HeaderContainer>
  );
};
